import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Camera, Trash, Crown } from "@phosphor-icons/react";
import { confirmDialog, alertDialog, promptDialog } from "@/lib/confirm";

/* ============================================================================
   AvatarUploader — shared profile-photo control used on the COE Member
   Profile and the staff self-profile. Renders the avatar in a circle, exposes
   a hover-to-show camera button, and supports clearing back to the gravatar
   fallback (admin-only path).

   Props:
     - src:           current photo URL ("" means no upload — falls back to initials)
     - name:          for the initials placeholder
     - size:          px (default 96)
     - uploadPath:    POST endpoint, e.g. "/admin/coe/people/{id}/photo"
     - clearPath:     optional DELETE endpoint to remove the upload
     - canEdit:       boolean — gates the upload UI
     - onUpdated:     callback fired with the new image URL (or null on clear)
   ========================================================================== */
const initials = (n) =>
    (n || "?").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

export function AvatarUploader({
    src,
    name,
    size = 96,
    uploadPath,
    clearPath,
    canEdit = true,
    onUpdated,
    isAdminBadge = false,
    testid = "coe-avatar-uploader",
}) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [errored, setErrored] = useState(false);

    const px = `${size}px`;
    const showImg = !!src && !errored;

    const onPick = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (f.size > 2 * 1024 * 1024) {
            toast.error("Image is too large (max 2 MB).");
            return;
        }
        if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
            toast.error("JPEG, PNG, or WebP only.");
            return;
        }
        const data = new FormData();
        data.append("file", f);
        setUploading(true);
        setErrored(false);
        try {
            const r = await api.post(uploadPath, data, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Photo updated");
            onUpdated?.(r.data?.image || r.data?.url || null);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        }
        setUploading(false);
    };

    const onClear = async () => {
        if (!clearPath) return;
        if (!await confirmDialog({ title: "Remove the uploaded photo? The Gravatar fallback will be used.", confirmText: "Confirm", tone: "danger" })) return;
        setUploading(true);
        try {
            await api.delete(clearPath);
            toast.success("Photo cleared");
            onUpdated?.(null);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
        setUploading(false);
    };

    return (
        <div className="relative group shrink-0" data-testid={testid}>
            <div
                className="rounded-full overflow-hidden ring-2 ring-primary/10 bg-secondary grid place-items-center"
                style={{ width: px, height: px }}
            >
                {showImg ? (
                    <img
                        src={src}
                        alt={name || "Avatar"}
                        onError={() => setErrored(true)}
                        className="w-full h-full object-cover"
                        data-testid={`${testid}-img`}
                    />
                ) : (
                    <span
                        className="text-primary font-display font-bold"
                        style={{ fontSize: `${Math.max(14, size * 0.36)}px` }}
                        data-testid={`${testid}-initials`}
                    >
                        {initials(name)}
                    </span>
                )}
            </div>

            {isAdminBadge && (
                <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-amber-100 text-amber-700 grid place-items-center ring-2 ring-card">
                    <Crown size={11} weight="fill" />
                </span>
            )}

            {canEdit && (
                <>
                    <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary text-white grid place-items-center shadow-md ring-2 ring-card hover:bg-primary/90 disabled:opacity-60 transition"
                        title={uploading ? "Uploading…" : "Upload photo"}
                        data-testid={`${testid}-pick-btn`}
                    >
                        <Camera size={14} weight="fill" />
                    </button>
                    {clearPath && showImg && !uploading && (
                        <button
                            type="button"
                            onClick={onClear}
                            className="absolute -bottom-1 left-0 w-7 h-7 rounded-full bg-card border border-border text-rose-600 grid place-items-center opacity-0 group-hover:opacity-100 transition hover:bg-rose-50"
                            title="Remove uploaded photo"
                            data-testid={`${testid}-clear-btn`}
                        >
                            <Trash size={11} />
                        </button>
                    )}
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={onPick}
                        data-testid={`${testid}-input`}
                    />
                </>
            )}
        </div>
    );
}
